import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { HelpCircle, RefreshCw, Save, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export function EightQuestionsView({ scene, onUpdate, onNavigate }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [items, setItems] = useState(scene.eight_questions || []);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/eight-questions`);
      setItems(r.data.pytania);
      onUpdate?.({ eight_questions: r.data.pytania });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/scenes/${scene.scene_id}/eight-questions`, { pytania: items });
      onUpdate?.({ eight_questions: items });
      toast.success(t("eight.saved"));
    } catch { toast.error(t("mir.fail")); }
    finally { setSaving(false); }
  };

  const updateAnswer = (i, val) => setItems(is => is.map((q, idx) => idx === i ? { ...q, a: val } : q));

  return (
    <div className="max-w-3xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap">{t("eight.eyebrow")}</div>
          <h2 className="serif text-3xl text-stone-100 mt-1">{t("eight.title")}</h2>
          <p className="text-stone-400 text-sm mt-2">{t("eight.subtitle")}</p>
        </div>
        <button data-testid="eight-generate-btn" onClick={generate} disabled={busy} className="pill-btn">
          {busy ? <><RefreshCw size={14} className="animate-spin"/> {t("eight.generating")}</> : (items.length ? <><RefreshCw size={14}/> {t("eight.regenerate")}</> : <><HelpCircle size={14}/> {t("eight.generate")}</>)}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="paper p-10 text-center mt-8">
          <HelpCircle className="mx-auto text-amber-500"/>
          <div className="text-stone-400 text-sm mt-3">{t("eight.empty")}</div>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
              className="paper p-5"
              data-testid={`eight-item-${i}`}
            >
              <div className="mono text-[11px] tracking-widest text-amber-500 uppercase">Q{String(i+1).padStart(2,"0")}</div>
              <div className="serif text-xl text-stone-100 mt-1">{q.q}</div>
              <textarea
                data-testid={`eight-answer-${i}`}
                value={q.a || ""}
                onChange={(e)=>updateAnswer(i, e.target.value)}
                rows={3}
                className="script-input mono text-sm mt-3"
                placeholder={t("eight.answer_ph")}
              />
            </motion.div>
          ))}
          <button data-testid="eight-save-btn" onClick={save} disabled={saving} className="pill-btn">
            <Save size={14}/> {saving ? t("new.saving") : t("eight.save")}
          </button>
        </div>
      )}

      {onNavigate && (
        <div className="mt-8 pt-6 border-t border-stone-800/60 flex justify-end">
          <button data-testid="nav-to-vagus-btn" onClick={() => onNavigate("vagus")} className="pill-btn">
            {t("nav.to_vagus")} <ArrowRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
